import { Trans } from '@lingui/react'
import classNames from 'clsx'
import { arrayOf, bool, func, node, number, shape, string } from 'prop-types'

import './CartSumBox.scss'

import {
  EXPERIMENT_VARIATION,
  TOTAAL_KORTING_AB_TEST,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import noop from 'shared/utils/noop'
import { APPLIED_PROMOTION_INFORMATION_MODAL } from 'views/components/organisms/CartSumBox/AppliedPromotionInformationModal/names'
import { ERxMedicationType } from 'views/consts/cart'
import { useLinePercentPromotions } from 'views/enhancers/useLinePercentPromotions'

import CheckoutButton from './CheckoutButton'
import CollectiveOrderBox from './CollectiveOrderBox'
import CostDetails from './CostDetails'
import CostDetailsBox from './CostDetailsBox'
import CreditNote from './CreditNote'
import ErxSumBox from './ErxSumBox'
import PrescriptionFollowsBox from './PrescriptionFollowsBox'
import SubTotal from './SubTotal'
import SumBoxAGB from './SumBoxAGB'
import SumBoxZavaCb from './SumBoxZavaCb'
import TotalSumBox from './TotalSumBox'

const CartSumBox = ({
  button,
  className,
  currency,
  costDetails,
  productAmount,
  rxCreditNoteAmount,
  totalAmountDiscounts,
  showAgb,
  showMultiOrderCheckbox,
  showPrescriptionFollowsCheckbox,
  showZavaCb,
  totalAmount,
  showFatBorder,
  vouchers,
  rxBonusDiscount,
  rxBonusToAccount,
  applicableCredit,
  showTotalAmountPrescriptionBonusToAccount,
  submitButtonAction,
  disableSubmitButton,
  sellerGroups,
  noModal,
  hasErxPriceSumBox,
  sellerVouchers,
}) => {
  const { linePercentPromotions } = useLinePercentPromotions()
  const linePercentPromotion = linePercentPromotions[0]

  const linePercentExperiment = useGetExperiment(TOTAAL_KORTING_AB_TEST)
  const showLineDiscountValueAboveSubtotal =
    linePercentExperiment.isEnabled &&
    (linePercentExperiment.variant === EXPERIMENT_VARIATION.V1 ||
      linePercentExperiment.variant === EXPERIMENT_VARIATION.V2) &&
    linePercentPromotion &&
    linePercentPromotion.value

  const groupEntriesERx =
    hasErxPriceSumBox &&
    sellerGroups
      .find(group => group?.groupEntriesERx)
      ?.groupEntriesERx.find(
        item =>
          item.type === 'insurancePrescription' ||
          item.type === 'privatePrescription'
      )

  const hasNonPznErx =
    hasErxPriceSumBox &&
    groupEntriesERx?.items?.some(item =>
      [
        ERxMedicationType.FREETEXT,
        ERxMedicationType.ACTIVE_SUBSTANCE,
        ERxMedicationType.COMPOSITE,
      ].includes(item.medicationType)
    )

  const vouchersBySellerName =
    sellerVouchers ??
    sellerGroups.map(seller => ({
      sellerName: seller.sellerName,
      vouchers: seller.discounts.map(({ code, origin }) => ({
        code,
        origin,
      })),
    }))

  return (
    <div
      data-qa-id="cartSumBox"
      className={classNames('o-CartSumBox', className)}
    >
      {showLineDiscountValueAboveSubtotal && (
        <CostDetails
          currency={currency}
          label={linePercentPromotion.code}
          value={-linePercentPromotion.value}
          modal={!noModal && APPLIED_PROMOTION_INFORMATION_MODAL}
        />
      )}
      <SubTotal productAmount={productAmount} currency={currency} />
      <CostDetailsBox
        vouchers={vouchers}
        costDetails={costDetails}
        rxBonusDiscount={rxBonusDiscount}
        applicableCredit={applicableCredit}
        currency={currency}
        noModal={noModal}
        vouchersBySellers={vouchersBySellerName}
      />

      {groupEntriesERx && <ErxSumBox groupEntriesERx={groupEntriesERx} />}
      {showFatBorder ? (
        <div className="o-CartSumBox__fatBorder u-margin-large--bottom u-margin-large--top" />
      ) : (
        <hr className="u-margin-large--bottom u-margin-large--top o-CartSumBox__slimBorder" />
      )}
      <TotalSumBox
        totalAmount={totalAmount}
        currency={currency}
        totalAmountDiscounts={totalAmountDiscounts}
        hasNonPznErx={hasNonPznErx}
      />
      {hasNonPznErx && (
        <p className="o-CartSumBox__sub-text u-margin-large--bottom">
          <Trans id="cart.sumBox.erx.priceInSeparateInvoice" />
        </p>
      )}
      <CreditNote
        rxCreditNoteAmount={rxCreditNoteAmount}
        rxBonusToAccount={rxBonusToAccount}
        showTotalAmountPrescriptionBonusToAccount={
          showTotalAmountPrescriptionBonusToAccount
        }
        currency={currency}
        sellerGroups={sellerGroups}
      />
      {showAgb && <SumBoxAGB />}
      {showZavaCb && <SumBoxZavaCb />}
      {button && (
        <CheckoutButton
          button={button}
          submitButtonAction={submitButtonAction}
          disableSubmitButton={disableSubmitButton}
        />
      )}
      {showMultiOrderCheckbox && <CollectiveOrderBox />}
      {showPrescriptionFollowsCheckbox && <PrescriptionFollowsBox />}
    </div>
  )
}

CartSumBox.propTypes = {
  button: shape({
    label: node.isRequired,
    id: string.isRequired,
    clickHandler: func,
    href: string,
  }),
  className: string,
  currency: string,
  costDetails: arrayOf(
    shape({
      id: string,
      label: string.isRequired,
      value: number.isRequired,
      modal: string,
    })
  ),
  productAmount: number.isRequired,
  rxCreditNoteAmount: number,
  totalAmountDiscounts: number,
  showAgb: bool,
  showMultiOrderCheckbox: bool,
  showPrescriptionFollowsCheckbox: bool,
  showZavaCb: bool,
  totalAmount: number.isRequired,
  showFatBorder: bool,
  bonusLabel: shape({
    transKey: string.isRequired,
    bonusesAmount: number.isRequired,
  }),
  vouchers: arrayOf(shape({})),
  rxBonusDiscount: number,
  rxBonusToAccount: number,
  applicableCredit: number,
  showTotalAmountPrescriptionBonusToAccount: bool,
  submitButtonAction: func,
  disableSubmitButton: bool,
  sellerGroups: [],
  noModal: bool,
  hasErxPriceSumBox: bool,
  sellerVouchers: arrayOf(shape({})),
}

CartSumBox.defaultProps = {
  button: null,
  className: '',
  currency: 'EUR',
  costDetails: [],
  vouchers: [],
  rxCreditNoteAmount: 0,
  totalAmountDiscounts: 0,
  showAgb: false,
  showMultiOrderCheckbox: false,
  showPrescriptionFollowsCheckbox: false,
  showZavaCb: false,
  showFatBorder: false,
  bonusLabel: null,
  rxBonusDiscount: 0,
  rxBonusToAccount: 0,
  showTotalAmountPrescriptionBonusToAccount: false,
  submitButtonAction: noop,
  disableSubmitButton: false,
  sellerGroups: [],
  showApplicableCredit: false,
  noModal: false,
  hasErxPriceSumBox: false,
}

export default CartSumBox
